import { useState, useEffect, useCallback, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useGetIdentity } from '@refinedev/core';

export interface TutorNotification {
  tutorId: string;
  dedupeKey: string;
  type: string;
  title: string;
  body: string;
  link: string;
  read: boolean;
  dismissed?: boolean;
  createdAt: string;
}

const GET_NOTIFICATIONS = /* GraphQL */ `
  query GetNotifications {
    getNotifications {
      tutorId
      dedupeKey
      type
      title
      body
      link
      read
      dismissed
      createdAt
    }
  }
`;

const MARK_NOTIFICATION_READ = /* GraphQL */ `
  mutation MarkNotificationRead($dedupeKey: String!) {
    markNotificationRead(dedupeKey: $dedupeKey) {
      tutorId
      dedupeKey
      read
    }
  }
`;

const DISMISS_NOTIFICATION = /* GraphQL */ `
  mutation DismissNotification($dedupeKey: String!) {
    dismissNotification(dedupeKey: $dedupeKey)
  }
`;

const ON_NOTIFICATION_CREATED = /* GraphQL */ `
  subscription OnNotificationCreated($tutorId: ID!) {
    onNotificationCreated(tutorId: $tutorId) {
      tutorId
      dedupeKey
      type
      title
      body
      link
      read
      dismissed
      createdAt
    }
  }
`;

interface UseNotificationsReturn {
  notifications: TutorNotification[];
  unreadCount: number;
  loading: boolean;
  markRead: (dedupeKey: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (dedupeKey: string) => Promise<void>;
  clearAll: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<TutorNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const { data: identity } = useGetIdentity<{ id: string }>();
  // Typed as any to avoid excessive-depth TS errors on Amplify's deep generics
  const clientRef = useRef<any>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  const getClient = useCallback((): any => {
    if (!clientRef.current) {
      clientRef.current = generateClient();
    }
    return clientRef.current;
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const client = getClient();
      const result = await client.graphql({ query: GET_NOTIFICATIONS }) as any;
      const items: TutorNotification[] = result?.data?.getNotifications || [];
      setNotifications(items.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  }, [getClient]);

  // Subscribe to new notifications when tutorId is available
  useEffect(() => {
    const tutorId = identity?.id;
    if (!tutorId) return;

    fetchNotifications();

    const client = getClient();
    try {
      const sub = (client.graphql({
        query: ON_NOTIFICATION_CREATED,
        variables: { tutorId },
      }) as any).subscribe({
        next: ({ data }: any) => {
          const notification: TutorNotification = data?.onNotificationCreated;
          if (notification) {
            setNotifications((prev) => {
              const exists = prev.some((n) => n.dedupeKey === notification.dedupeKey);
              if (exists) return prev;
              return [notification, ...prev];
            });
          }
        },
        error: (err: any) => {
          console.error('Notification subscription error:', err);
        },
      });
      subscriptionRef.current = sub;
    } catch (e) {
      console.error('Failed to subscribe to notifications:', e);
    }

    return () => {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [identity?.id, fetchNotifications, getClient]);

  const markRead = useCallback(async (dedupeKey: string) => {
    try {
      const client = getClient();
      await client.graphql({ query: MARK_NOTIFICATION_READ, variables: { dedupeKey } });
      setNotifications((prev) =>
        prev.map((n) => (n.dedupeKey === dedupeKey ? { ...n, read: true } : n))
      );
    } catch (e) {
      console.error('Failed to mark notification read:', e);
    }
  }, [getClient]);

  const markAllRead = useCallback(async () => {
    const unread = notifications.filter((n) => !n.read);
    await Promise.all(unread.map((n) => markRead(n.dedupeKey)));
  }, [notifications, markRead]);

  const dismiss = useCallback(async (dedupeKey: string) => {
    try {
      const client = getClient();
      await client.graphql({ query: DISMISS_NOTIFICATION, variables: { dedupeKey } });
      setNotifications((prev) => prev.filter((n) => n.dedupeKey !== dedupeKey));
    } catch (e) {
      console.error('Failed to dismiss notification:', e);
    }
  }, [getClient]);

  const clearAll = useCallback(async () => {
    await Promise.all(notifications.map((n) => dismiss(n.dedupeKey)));
  }, [notifications, dismiss]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    dismiss,
    clearAll,
    refetch: fetchNotifications,
  };
}
